import Vue from "vue";
import { Store } from "vuex";
import { Action, Module, Mutation, VuexModule, getModule } from "vuex-module-decorators";

import { matchesCaseInsensitive } from "@/utils/comparators/string";
import logger, { logLevel } from "@/utils/logger";

export const DEFAULT_NONPOST_LIMIT = 3;

export const localStorageKeyMap: Readonly<{ [key in SearchType]: string }> = {
  post: "previousSearches",
  profile: "previousCreators",
} as const;

@Module({
  name: "search",
  stateFactory: true,
  namespaced: true,
})
export default class SearchStore extends VuexModule {
  // Previous Searches
  historyMap: Record<SearchType, SearchSuggestion[]> = {
    post: [],
    profile: [],
  };

  get historyTypeItem() {
    return <T extends SearchType>(type: T): SearchSuggestion[] => this.historyMap[type];
  }

  @Action({ rawError: true })
  fetchSearchHistory() {
    if (process.server) {
      return;
    }

    // Setting state _after_ hydration replaces the sate from the server. Since this data is stored between sessions in localStorage,
    // it must be initialized client side and after server state has been set or else it will be emptied out
    (["post", "profile"] as const).forEach((type) => {
      const data = localStorage.getItem(localStorageKeyMap[type]);
      let response: SearchSuggestion[] = [];
      try {
        response = JSON.parse(data ?? "[]");
      } catch (error) {
        logger(logLevel.WARN, "Malformed JSON for search history", { type, data }, error);
      }

      if (Array.isArray(response) === false) {
        logger(logLevel.LOG, "Unexpected response format. Expected array", { response, type });
        return;
      }

      const searchHistory = response.slice(0, DEFAULT_NONPOST_LIMIT);

      this.setSearchHistory({ type, searchHistory });
    });
  }

  @Action({ rawError: true })
  addSearchHistory({ searchItem, type }: { searchItem: SearchSuggestion; type: SearchType }) {
    // We can't save previous searches on the server, and it causes an error
    if (process.server || !searchItem) {
      return;
    }

    const locale = this.store.$i18n.locale;

    // Remove an existing entry from the existing history as it will get promoted to the beginning of the list
    const searchHistory = this.historyTypeItem(type)
      .filter(
        ({ objectID, query }) =>
          matchesCaseInsensitive(objectID, searchItem.objectID, locale) === false &&
          matchesCaseInsensitive(query, searchItem.query, locale) === false,
      )
      // Slice off one more than needed to allow for adding the new item at the beginning
      .slice(0, DEFAULT_NONPOST_LIMIT - 1);

    searchHistory.unshift(searchItem);

    this.setSearchHistory({ searchHistory, type });
  }

  @Mutation
  setSearchHistory({ searchHistory, type }: { searchHistory: SearchSuggestion[]; type: SearchType }) {
    Vue.set(this.historyMap, type, searchHistory);
    localStorage.setItem(localStorageKeyMap[type], JSON.stringify(searchHistory));
  }
}

export const getSearchStore = (store: Store<unknown>) => {
  const searchStore = getModule(SearchStore, store);
  return searchStore;
};
